import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  HandCoins, Plus, Search, Check, AlertTriangle,
  Calendar, IndianRupee, Clock
} from 'lucide-react';
import AdminLayout from '../../components/layouts/AdminLayout';
import { apiClient } from '../../App';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const ContributionsPage = () => {
  const [contributions, setContributions] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    member_id: '',
    month: new Date().toISOString().slice(0, 7),
    amount: 1000,
    status: 'pending'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contribRes, membersRes] = await Promise.all([
        apiClient.get('/contributions'),
        apiClient.get('/members')
      ]);
      setContributions(contribRes.data);
      setMembers(membersRes.data.filter(m => m.is_active));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const handleAddContribution = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/contributions', formData);
      toast.success('Contribution added successfully');
      setShowAddModal(false);
      setFormData({ member_id: '', month: new Date().toISOString().slice(0, 7), amount: 1000, status: 'pending' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add contribution');
    }
  };

  const handleMarkPaid = async (contributionId) => {
    try {
      await apiClient.put(`/contributions/${contributionId}?status=paid`);
      toast.success('Contribution marked as paid');
      fetchData();
    } catch (error) {
      toast.error('Failed to update contribution');
    }
  };

  const handleBulkAdd = async () => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    let added = 0;
    
    for (const member of members) {
      const exists = contributions.find(c => c.member_id === member.id && c.month === currentMonth);
      if (!exists) {
        try {
          await apiClient.post('/contributions', {
            member_id: member.id,
            month: currentMonth,
            amount: 1000,
            status: 'pending'
          });
          added++;
        } catch (error) {
          console.error(`Failed to add contribution for ${member.name}`);
        }
      }
    }
    
    if (added > 0) {
      toast.success(`Added ${added} contributions for ${currentMonth}`);
      fetchData();
    } else {
      toast.info('All members already have contributions for this month');
    }
  };

  const filteredContributions = contributions.filter(contrib => {
    const matchesSearch = contrib.member_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || contrib.status === statusFilter;
    const matchesMonth = !monthFilter || contrib.month === monthFilter;
    return matchesSearch && matchesStatus && matchesMonth;
  });

  const uniqueMonths = [...new Set(contributions.map(c => c.month))].sort().reverse();

  const stats = {
    total: filteredContributions.length,
    paid: filteredContributions.filter(c => c.status === 'paid').length,
    pending: filteredContributions.filter(c => c.status === 'pending').length,
    late: filteredContributions.filter(c => c.status === 'late').length,
    totalAmount: filteredContributions.filter(c => ['paid', 'late'].includes(c.status)).reduce((sum, c) => sum + c.amount, 0),
    totalPenalties: filteredContributions.reduce((sum, c) => sum + (c.penalty_amount || 0), 0)
  };

  return (
    <AdminLayout title="Contributions" subtitle="Track monthly member contributions">
      {/* Add Contribution Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Contribution</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddContribution} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="member">Member</Label>
              <Select value={formData.member_id} onValueChange={(v) => setFormData({ ...formData, member_id: v })}>
                <SelectTrigger data-testid="contrib-member-select">
                  <SelectValue placeholder="Select member" />
                </SelectTrigger>
                <SelectContent>
                  {members.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="month">Month</Label>
              <Input
                id="month"
                type="month"
                value={formData.month}
                onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                data-testid="contrib-month-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="amount">Amount (₹)</Label>
              <Input
                id="amount"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                data-testid="contrib-amount-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger data-testid="contrib-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#0A2540] hover:bg-[#163A5F]" data-testid="contrib-submit-btn">
                Add Contribution
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
        <div className="flex gap-4 flex-1 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#425466]" />
            <Input
              placeholder="Search by member..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-contributions-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32" data-testid="contrib-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="late">Late</SelectItem>
            </SelectContent>
          </Select>
          <Select value={monthFilter} onValueChange={setMonthFilter}>
            <SelectTrigger className="w-40" data-testid="contrib-filter-month">
              <SelectValue placeholder="All months" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">All months</SelectItem>
              {uniqueMonths.map(month => (
                <SelectItem key={month} value={month}>{month}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={handleBulkAdd}
            data-testid="bulk-add-contrib-btn"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Add Monthly
          </Button>
          <Button 
            onClick={() => setShowAddModal(true)}
            className="bg-[#0A2540] hover:bg-[#163A5F]"
            data-testid="add-contrib-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Single
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <div className="dashboard-card p-4">
          <p className="text-xs text-[#425466] mb-1">Total</p>
          <p className="text-xl font-bold text-[#0A2540]">{stats.total}</p>
        </div>
        <div className="dashboard-card p-4">
          <p className="text-xs text-[#425466] mb-1">Paid</p>
          <p className="text-xl font-bold text-green-600">{stats.paid}</p>
        </div>
        <div className="dashboard-card p-4">
          <p className="text-xs text-[#425466] mb-1">Pending</p>
          <p className="text-xl font-bold text-yellow-600">{stats.pending}</p>
        </div>
        <div className="dashboard-card p-4">
          <p className="text-xs text-[#425466] mb-1">Late</p>
          <p className="text-xl font-bold text-red-600">{stats.late}</p>
        </div>
        <div className="dashboard-card p-4">
          <p className="text-xs text-[#425466] mb-1">Collected</p>
          <p className="text-xl font-bold text-[#00C853]">{formatCurrency(stats.totalAmount)}</p>
        </div>
        <div className="dashboard-card p-4">
          <p className="text-xs text-[#425466] mb-1">Penalties</p>
          <p className="text-xl font-bold text-red-500">{formatCurrency(stats.totalPenalties)}</p>
        </div>
      </div>

      {/* Contributions Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="dashboard-card overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#F4F7FB] border-b border-gray-100">
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Member</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Month</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Amount</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Due Date</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Penalty</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-32 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-20 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-20 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-24 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-16 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-16 rounded-full"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-8 w-20 rounded"></div></td>
                  </tr>
                ))
              ) : filteredContributions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-[#425466]">
                    <HandCoins className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No contributions found</p>
                  </td>
                </tr>
              ) : (
                filteredContributions.map((contrib) => (
                  <tr key={contrib.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#0A2540] flex items-center justify-center text-white text-sm font-medium">
                          {contrib.member_name.charAt(0)}
                        </div>
                        <span className="font-medium text-[#0A2540]">{contrib.member_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-[#425466]">{contrib.month}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-semibold text-[#0A2540]">{formatCurrency(contrib.amount)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-[#425466] text-sm">{new Date(contrib.due_date).toLocaleDateString('en-IN')}</span>
                    </td>
                    <td className="px-6 py-4">
                      {contrib.penalty_amount > 0 ? (
                        <span className="text-red-600 font-medium">{formatCurrency(contrib.penalty_amount)}</span>
                      ) : (
                        <span className="text-[#425466]">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        contrib.status === 'paid' ? 'bg-green-100 text-green-600' :
                        contrib.status === 'late' ? 'bg-red-100 text-red-600' :
                        'bg-yellow-100 text-yellow-600'
                      }`}>
                        {contrib.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {contrib.status === 'pending' && (
                        <Button 
                          size="sm"
                          onClick={() => handleMarkPaid(contrib.id)}
                          className="bg-[#00C853] hover:bg-[#009624] h-8"
                          data-testid={`mark-paid-${contrib.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Mark Paid
                        </Button>
                      )}
                      {contrib.status === 'paid' && contrib.paid_date && (
                        <span className="text-xs text-[#425466]">
                          Paid: {new Date(contrib.paid_date).toLocaleDateString('en-IN')}
                        </span>
                      )}
                      {contrib.status === 'late' && (
                        <div className="flex items-center gap-1 text-red-600">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="text-xs">Late</span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </AdminLayout>
  );
};

export default ContributionsPage;
